import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LocalizationProvider } from './features/localization/LocalizationContext';
import { ClientRoutes } from './app/routes/ClientRoutes';
import { AdminRoutes } from './app/routes/AdminRoutes';
import { WorkspaceRoutes } from './app/routes/WorkspaceRoutes';
import { DEFAULT_WORKSPACE_PATH, ONBOARDING_PATH } from './features/workspace/navigation';
import Login from './features/auth/pages/Login';
import SignUp from './features/auth/pages/SignUp';
import ResetPassword from './features/auth/pages/ResetPassword';
import AdminLogin from './features/auth/pages/AdminLogin';
import Settings from './features/shared/pages/Settings';
import TripDetails from './features/client/pages/TripDetails';
import MarketplaceHome from './pages/MarketplaceHome';
import BrowseListings from './pages/BrowseListings';
import About from './pages/About';
import Pricing from './pages/Pricing';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Support from './pages/Support';
import DeleteAccount from './pages/DeleteAccount';
import TermsOfService from './pages/TermsOfService';
import PublicBooking from './pages/PublicBooking';
import { RequireAuth } from './app/guards/RequireAuth';
import AppLayout from './components/AppLayout';
import { hasRole } from './services/auth';
import ScrollToTop from './components/ScrollToTop';

// Component to redirect hosts to the host app
const HostRedirect = () => {
  const { user, loading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!loading) {
      if (user && hasRole(user, 'host')) {
        // Redirect to host app (gathur-landing)
        const hostAppUrl = process.env.REACT_APP_HOST_APP_URL || 'http://localhost:3001';
        window.location.href = `${hostAppUrl}${location.pathname}`;
      } else {
        // Redirect to login if not authenticated
        window.location.href = '/login';
      }
    }
  }, [user, loading, location]);

  return <AppLoading />;
};

const AppLoading = () => (
  <div className="flex min-h-screen items-center justify-center bg-sand-100">
    <div className="h-12 w-12 animate-spin rounded-full border-2 border-ink-900/20 border-t-saffron-500" />
  </div>
);

// Component to handle post-login routing
const AppRouter = () => {
  const { user, loading } = useAuth();

  // Routing logic - no verbose logging

  if (loading) {
    return <AppLoading />;
  }

  return (
    <Routes>

      {/* Public routes */}
      <Route 
        path="/" 
        element={
          !user ? (
            <MarketplaceHome />
          ) : hasRole(user, 'admin') ? (
            <Navigate to="/admin" replace />
          ) : (
            <Navigate to={DEFAULT_WORKSPACE_PATH} replace />
          )
        } 
      />
      <Route path="/about" element={<About />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/support" element={<Support />} />
      <Route path="/delete-account" element={<DeleteAccount />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/terms" element={<Navigate to="/terms-of-service" replace />} />
      <Route path="/book/:slug" element={<PublicBooking />} />
      <Route path="/available-listings" element={<BrowseListings />} />
      <Route path="/trips/:tripId" element={<TripDetails />} />
      <Route 
        path="/login" 
        element={
          !user ? (
            <Login />
          ) : (
            <Navigate to={DEFAULT_WORKSPACE_PATH} replace />
          )
        } 
      />
      <Route 
        path="/signup" 
        element={
          !user ? (
            <SignUp />
          ) : (
            <Navigate to={ONBOARDING_PATH} replace />
          )
        } 
      />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/adminLogin" element={<AdminLogin />} />

      {/* Shared protected routes */}
      <Route 
        path="/settings" 
        element={
          <RequireAuth>
            <AppLayout>
              <Settings />
            </AppLayout>
          </RequireAuth>
        } 
      />
      
      {/* Authenticated trip details route - for booking and management */}
      <Route 
        path="/client/trips/:tripId" 
        element={
          <RequireAuth>
            <AppLayout>
              <TripDetails />
            </AppLayout>
          </RequireAuth>
        } 
      />

      {/* Signed-in business workspace */}
      <Route path="/app/*" element={<WorkspaceRoutes />} />

      {/* Demo / short URLs → open Business Agent dock on Front Desk */}
      <Route
        path="/business-agent"
        element={<Navigate to="/app/front-desk/today?agent=1" replace />}
      />
      <Route path="/sera" element={<Navigate to="/app/front-desk/today?agent=1" replace />} />

      {/* Protected routes - RequireRole guard handles authentication/authorization */}
      <Route path="/client/*" element={<ClientRoutes />} />
      {/* Host routes moved to gathur-landing project - redirect hosts */}
      <Route 
        path="/host/*" 
        element={<HostRedirect />} 
      />
      <Route path="/admin/*" element={<AdminRoutes />} />

      {/* Authenticated user redirects */}
      {user && user.role && (
        <Route
          path="/dashboard"
          element={
            hasRole(user, 'admin') ? (
              <Navigate to="/admin" replace />
            ) : (
              <Navigate to={DEFAULT_WORKSPACE_PATH} replace />
            )
          }
        />
      )}

      {/* Catch all - redirect to marketplace home */}
      <Route 
        path="*" 
        element={<Navigate to="/" replace />} 
      />
    </Routes>
  );
};

function App() {
  return (
    <Router>
      <ScrollToTop />
      <LocalizationProvider>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </LocalizationProvider>
    </Router>
  );
}

export default App;
